<script lang="ts">
  import { gameStore } from '../../stores/gameStore';
  import { playerStore } from '../../stores/playerStore';
  import { socketService } from '../../services/socketService';
  import { soundService } from '../../services/soundService';
  import { announceToScreenReader } from '../../lib/accessibility';
  import { SoundCategory } from '../../types/audio';
  import type { PromptOption } from '@blather/shared';

  let hasRequested = false;
  let hasRefreshed = false;
  let selectedPromptIndex: number | null = null;
  let visibleHints: Set<number> = new Set();

  $: prompts = $gameStore.availablePrompts;
  $: myPlayerId = $playerStore.player?.id;
  $: myPromptData = $gameStore.gameState?.promptSelection?.find(
    (pp) => pp.playerId === myPlayerId
  );
  $: hasSelected = myPromptData?.prompt != null; // Checks for both null and undefined
  $: canRefresh = !hasRefreshed && !myPromptData?.hasRefreshed && !hasSelected;

  // Request prompts when component mounts
  $: if (!hasRequested && $gameStore.gameState?.phase === 'prompt_selection') {
    requestPrompts();
  }

  function requestPrompts() {
    hasRequested = true;
    socketService.emit('game:requestPrompts');
    announceToScreenReader('Requesting prompts', 'polite');
  }

  function refreshPrompts() {
    if (!canRefresh) return;

    hasRefreshed = true;
    socketService.emit('game:refreshPrompts');
    announceToScreenReader('Refreshing prompts', 'polite');

    // Play UI notification sound
    soundService.play('/audio/ui/notification.ogg', {
      category: SoundCategory.UI,
    });
  }

  function toggleHint(index: number, event: Event) {
    event.stopPropagation(); // Prevent card click
    if (visibleHints.has(index)) {
      visibleHints.delete(index);
      announceToScreenReader('Hint hidden', 'polite');
    } else {
      visibleHints.add(index);
      const hint = prompts[index].prompt.explanation;
      announceToScreenReader(`Hint: ${hint}`, 'polite');
    }
    visibleHints = visibleHints; // Trigger reactivity
  }

  function selectPrompt(index: number) {
    if (hasSelected) return;

    selectedPromptIndex = index;
    const selectedOption = prompts[index];

    // Send the actual prompt data, not just the index
    socketService.emit('game:selectPrompt', {
      promptWord: selectedOption.prompt.word,
      category: selectedOption.category,
    });

    announceToScreenReader(
      `You have selected ${selectedOption.prompt.word}`,
      'assertive'
    );

    // Play selection sound
    soundService.play('/audio/sfx/prompt-select.ogg', {
      category: SoundCategory.SFX,
    });
  }

  // Announce when prompts arrive
  $: if (prompts.length > 0 && !hasSelected) {
    announceToScreenReader(
      `${prompts.length} prompts available. Use arrow keys to navigate and Enter to select.`,
      'polite'
    );
  }
</script>

<div class="prompt-selection">
  <h2>Select Your Prompt</h2>

  {#if hasSelected}
    <div class="selected-message" role="status">
      <p>You selected: <strong>{myPromptData?.prompt?.word}</strong></p>
      <p class="wait-message">Waiting for other players to select their prompts...</p>
    </div>
  {:else if prompts.length === 0}
    <div class="loading-message" role="status" aria-live="polite">
      <p>Loading prompts...</p>
    </div>
  {:else}
    <div class="prompts-container">
      {#each prompts as promptOption, index}
        <div class="prompt-card-wrapper">
          <button
            class="prompt-card"
            class:selected={selectedPromptIndex === index}
            on:click={() => selectPrompt(index)}
            aria-label={`Prompt ${index + 1}: ${promptOption.prompt.word}. Category: ${promptOption.category}`}
          >
            <div class="prompt-header">
              <span class="prompt-number">#{index + 1}</span>
              <span class="prompt-category">{promptOption.category}</span>
            </div>
            <div class="prompt-word">{promptOption.prompt.word}</div>
            {#if visibleHints.has(index)}
              <div class="prompt-explanation">{promptOption.prompt.explanation}</div>
            {/if}
          </button>
          <button
            class="hint-button"
            on:click={(e) => toggleHint(index, e)}
            aria-label={visibleHints.has(index) ? 'Hide hint' : 'Show hint'}
          >
            {visibleHints.has(index) ? '👁️ Hide Hint' : '💡 Show Hint'}
          </button>
        </div>
      {/each}
    </div>

    <div class="actions">
      <button
        class="refresh-button"
        on:click={refreshPrompts}
        disabled={!canRefresh}
        aria-label={hasRefreshed ? 'Already refreshed prompts' : 'Refresh prompts (one time only)'}
      >
        {hasRefreshed ? 'Already Refreshed' : 'Refresh Prompts (1x)'}
      </button>
    </div>
  {/if}
</div>

<style>
  .prompt-selection {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
  }

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    font-size: 2rem;
  }

  .loading-message,
  .selected-message {
    text-align: center;
    padding: 3rem;
  }

  .selected-message strong {
    font-size: 1.5rem;
    color: var(--color-primary, #4a9eff);
  }

  .wait-message {
    margin-top: 1rem;
    color: var(--color-text-muted, #666);
  }

  .prompts-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .prompt-card-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .prompt-card {
    background: white;
    border: 2px solid #ddd;
    border-radius: 8px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    flex: 1;
  }

  .hint-button {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .hint-button:hover {
    background: var(--color-bg-secondary, #f5f5f5);
    border-color: var(--color-primary, #4a9eff);
  }

  .hint-button:focus {
    outline: 2px solid var(--color-primary, #4a9eff);
    outline-offset: 2px;
  }

  .prompt-card:hover {
    border-color: var(--color-primary, #4a9eff);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .prompt-card:focus {
    outline: 3px solid var(--color-primary, #4a9eff);
    outline-offset: 2px;
  }

  .prompt-card.selected {
    border-color: var(--color-success, #4caf50);
    background: #f0f9f0;
  }

  .prompt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .prompt-number {
    font-weight: bold;
    color: var(--color-text-muted, #666);
  }

  .prompt-category {
    text-transform: capitalize;
    font-size: 0.875rem;
    padding: 0.25rem 0.75rem;
    background: var(--color-bg-secondary, #f5f5f5);
    border-radius: 12px;
    color: var(--color-text, #333);
  }

  .prompt-word {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: var(--color-text, #333);
  }

  .prompt-explanation {
    font-size: 0.9rem;
    color: var(--color-text-muted, #666);
    line-height: 1.4;
  }

  .actions {
    text-align: center;
  }

  .refresh-button {
    padding: 0.75rem 2rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .refresh-button:hover:not(:disabled) {
    background: var(--color-bg-secondary, #f5f5f5);
    border-color: var(--color-primary, #4a9eff);
  }

  .refresh-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .refresh-button:focus {
    outline: 3px solid var(--color-primary, #4a9eff);
    outline-offset: 2px;
  }
</style>
